/* Quiet-hours preferences hook.
 *
 * Lives in localStorage so anonymous users see their pickers instantly.
 * When the user is signed-in, also mirrors to the backend
 * (`/api/notification-prefs`) so the storm-watch sweeper can honour the
 * window even when the device is offline.
 *
 * Default: disabled, 23:00 → 07:00, browser-detected IANA timezone.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getNotificationPrefs, putNotificationPrefs } from "./api";

const STORAGE_KEY = "nowcast.quietHours.v1";

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
}

function defaults() {
  return {
    quiet_hours_enabled: false,
    quiet_start_min: 23 * 60,
    quiet_end_min: 7 * 60,
    timezone: detectTimezone(),
  };
}

function load() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...defaults(), ...JSON.parse(raw) };
  } catch {
    /* private mode / corrupted */
  }
  return defaults();
}

function persistLocal(prefs) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

export function useQuietHours() {
  const [prefs, setPrefs] = useState(() =>
    typeof window !== "undefined" ? load() : defaults(),
  );
  const saveTimer = useRef(null);
  const fetchedRef = useRef(false);

  // Hydrate from backend once on mount; falls through silently for
  // anon users (axios will 401, we just keep localStorage).
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getNotificationPrefs()
      .then((r) => {
        if (r?.prefs) {
          setPrefs((prev) => ({ ...prev, ...r.prefs }));
          persistLocal({ ...load(), ...r.prefs });
        }
      })
      .catch(() => {
        /* anon — keep local prefs */
      });
  }, []);

  const update = useCallback((patch) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch, timezone: prev.timezone || detectTimezone() };
      persistLocal(next);
      // Debounced backend sync — silent if not signed-in.
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        putNotificationPrefs(next).catch(() => {
          /* anon or offline — ignore */
        });
      }, 450);
      return next;
    });
  }, []);

  return { prefs, update };
}
